import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Checkbox, Colors } from 'shared/components/ui/checkbox';
import { NLPInput } from 'shared/components/ui/n-l-p-input';
import { useI18nLocale } from 'shared/lib/react-i18next/use-i18n-locale';
import { ID } from 'shared/types/id';
import { NewEntityTypes } from 'shared/types/nlp';
import { TaskPriorityOptions } from 'shared/types/task-base';
import { WordId } from 'shared/utils/nlp/get-ids-for-words';

import { useData } from '../../use-data';
import * as Styled from './main.style';

const priorityColorMap: Record<TaskPriorityOptions, Colors> = {
  [TaskPriorityOptions.Low]: Colors.Info,
  [TaskPriorityOptions.Medium]: Colors.Warning,
  [TaskPriorityOptions.High]: Colors.Error,
  [TaskPriorityOptions.None]: Colors.Default,
};

export type TaskDetailMainProps = {
  name: string;
  priority?: TaskPriorityOptions | null;
  updateName: (
    name: string,
    extraInfo: { wordIds: WordId[]; ignoredWordIds: ID[] },
  ) => void;
  onNewGoal: (name: string) => void;
  onNewTag: (name: string) => void;
  onPremium: () => void;
  onNameSubmit?: (
    name: string,
    extraInfo: { wordIds: WordId[]; ignoredWordIds: ID[] },
  ) => void;
};

export const CreateMain: React.FC<TaskDetailMainProps> = ({
  name,
  priority,
  onNewGoal,
  onNewTag,
  onPremium,
  updateName,
  onNameSubmit,
}) => {
  const titleRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();
  const locale = useI18nLocale();
  const {
    dateFormat,
    goals,
    lifeAreas,
    tags,
    createHabitTargetAllowed,
    createHabitAllowed,
    createGoalAllowed,
  } = useData();

  const preventPropagation = (e: React.MouseEvent) => e.stopPropagation();

  const _onNameSubmit = (
    name: string,
    extraInfo: { wordIds: WordId[]; ignoredWordIds: ID[] },
  ) => onNameSubmit?.(name, extraInfo);

  const onNewEntity = (type: NewEntityTypes, name: string) => {
    if (type === NewEntityTypes.Goal) {
      onNewGoal(name);
      return;
    }

    onNewTag(name);
  };

  useEffect(() => {
    titleRef.current?.focus();
  }, []);

  return (
    <Styled.Container>
      <Styled.TitleBar>
        <Styled.CheckboxContainer onClick={preventPropagation}>
          <Checkbox
            color={priority ? priorityColorMap[priority] : undefined}
            readOnly
          />
        </Styled.CheckboxContainer>

        <Styled.TitleContainer>
          <NLPInput
            ref={titleRef}
            value={name}
            goals={goals}
            lifeAreas={lifeAreas}
            tags={tags}
            language={locale}
            placeholder={t('general.labels.task-detail.title.placeholder')}
            dateFormat={dateFormat}
            createTargetHabitAllowed={createHabitTargetAllowed}
            createHabitAllowed={createHabitAllowed}
            createGoalAllowed={createGoalAllowed}
            onChange={updateName}
            onSubmit={_onNameSubmit}
            onPremium={onPremium}
            onBulkPaste={(...args) => {
              // eslint-disable-next-line no-console -- temp
              console.log('bulk paste', args);
            }}
            onNewEntity={onNewEntity}
          />
        </Styled.TitleContainer>
      </Styled.TitleBar>
    </Styled.Container>
  );
};
